package com.devi3east.bashbuddy.dashboard.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.devi3east.bashbuddy.auth.domain.models.UserType
import com.devi3east.bashbuddy.dashboard.domain.DashboardRepository
import com.devi3east.bashbuddy.features.events.common.data.models.TicketType
import com.devi3east.bashbuddy.features.events.requests.CreateDraftTicketRequest
import com.devi3east.bashbuddy.features.events.requests.UpdateDraftEventRequest
import com.devi3east.bashbuddy.features.events.requests.UpdateTicketRequest
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeRequest
import com.devi3east.bashbuddy.prizeList.requests.CreatePrizeSeriesRequest
import com.devi3east.bashbuddy.prizeList.requests.UpdatePrizeRequest
import com.devi3east.bashbuddy.purchase.domain.models.toUiModel
import com.devi3east.bashbuddy.purchase.requests.UpdateAllocationsRequest
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class DashboardViewModel(
    private val repository: DashboardRepository,
    private val tokenProvider: () -> String?
) : ViewModel() {

    private val _uiState = MutableStateFlow(DashboardUiState())
    val uiState: StateFlow<DashboardUiState> = _uiState

    fun createDraftEventAndOpenEdit() {
        val token = tokenProvider() ?: run {
            return
        }
        viewModelScope.launch {
            val newEventId = repository.createDraftEvent(token)
            if (newEventId != null) {
                _uiState.update { it.copy(eventToEditId = newEventId) }
                loadDashboardData()
                openEditEventDialog(newEventId)
            }
        }
    }

    fun createDraftTicketAndOpenEdit(eventId: String, request: CreateDraftTicketRequest) {
        val token = tokenProvider() ?: run {
            return
        }
        viewModelScope.launch {
            val newTicketId = repository.createDraftTicket(token, eventId, request)
            if (newTicketId != null) {
                _uiState.update { it.copy(ticketToUpdateId = newTicketId) }
                openUpdateTicketDialog(newTicketId)
                loadDashboardData()
            }
        }
    }

    fun openEditEventDialog(eventId: String) {
        val imageUrl = _uiState.value.eventImageMap[eventId]
        _uiState.update {
            it.copy(
                eventToEditId = eventId,
                isEditEventDialogVisible = true,
                eventImageUrl = imageUrl
            )
        }
    }

    fun openUpdateTicketDialog(ticketId: String) {
        val imageUrl = _uiState.value.ticketImageMap[ticketId]
        _uiState.update {
            it.copy(
                ticketToUpdateId = ticketId,
                isUpdateTicketDialogVisible = true,
                ticketImageUrl = imageUrl
            )
        }
    }

    fun dismissEditEventDialog() {
        _uiState.update { it.copy(isEditEventDialogVisible = false, eventToEditId = null) }
    }

    fun dismissEditTicketDialog() {
        _uiState.update { it.copy(isUpdateTicketDialogVisible = false, ticketToUpdateId = null) }
    }

    fun loadDashboardData() {
        val token = tokenProvider() ?: run {
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isDashboardLoading = true, errorMessage = null) }
            val result = repository.getDashboard(token)
            result.onSuccess { response ->
                val events = if (response.userType == UserType.ORGANIZATION) response.events else emptyList()
                val tickets = if (response.userType == UserType.ORGANIZATION) response.tickets else emptyList()
                val purchases = if (response.userType == UserType.USER) {
                    response.purchases.map { it.toUiModel(response.events, response.tickets) }
                } else emptyList()
                val prizes = if (response.userType == UserType.ORGANIZATION) response.prizes else emptyList()
                val printedTickets = tickets.filter { it.ticketType == TicketType.PRINTED }
                val pendingPerTicket: Map<String, Int> = printedTickets.associate { it._id to it.pendingActions }
                val pendingPerEvent: Map<String, Int> = printedTickets
                    .groupBy { it.eventId ?: "" }
                    .mapValues { entry -> entry.value.sumOf { it.pendingActions } }
                val eventImageMap = events.associate { it._id to (it.imageUrl ?: "") }
                val ticketImageMap = tickets.associate { it._id to (it.imageUrl ?: "") }
                _uiState.update {
                    it.copy(
                        isDashboardLoading = false,
                        eventList = events,
                        ticketList = tickets,
                        purchaseList = purchases,
                        prizeList = prizes,
                        errorMessage = null,
                        pendingAssignmentsPerTicket = pendingPerTicket,
                        pendingAssignmentsPerEvent = pendingPerEvent,
                        eventImageMap = eventImageMap,
                        ticketImageMap = ticketImageMap
                    )
                }
                /*if (response.userType == UserType.ORGANIZATION) {
                    tickets.forEach { ticket ->
                        loadAllocationsForTicket(ticket._id)
                    }
                }*/
            }.onFailure { e ->
                e.printStackTrace()
                _uiState.update { it.copy(isDashboardLoading = false, errorMessage = e.message ?: "Unknown error") }
            }
        }
    }

    fun updateEventAndRefresh(eventId: String, request: UpdateDraftEventRequest) {
        val token = tokenProvider() ?: return
        viewModelScope.launch {
            repository.updateDraftEvent(token, eventId, request)
            loadDashboardData()
        }
    }

    fun updateTicketAndRefresh(ticketId: String, request: UpdateTicketRequest) {
        val token = tokenProvider() ?: return
        viewModelScope.launch {
            repository.updateTicket(token, ticketId, request)
            loadDashboardData()
        }
    }

    fun togglePauseTicket(ticketId: String, pause: Boolean) {
        val token = tokenProvider() ?: return
        viewModelScope.launch {
            val success = repository.pauseTicket(token, ticketId, pause)
            if (success) {
                loadDashboardData()
            } else {
                // Intentionally left blank
            }
        }
    }

    fun confirmDeleteEvent(eventId: String) {
        _uiState.update { it.copy(eventToDeleteId = eventId, isDeleteEventConfirmDialogVisible = true) }
    }

    fun confirmDeleteTicket(ticketId: String) {
        _uiState.update { it.copy(ticketToDeleteId = ticketId, isDeleteTicketConfirmDialogVisible = true) }
    }

    fun dismissDeleteDialog() {
        _uiState.update {
            it.copy(
                isDeletePrizeDialogVisible = false,
                isDeleteTicketConfirmDialogVisible = false,
                isDeleteEventConfirmDialogVisible = false,
                ticketToDeleteId = null,
                eventToDeleteId = null
            )
        }
    }

    fun deleteTicketAndRefresh(ticketId: String) {
        val token = tokenProvider() ?: return
        viewModelScope.launch {
            val result = repository.deleteTicket(token, ticketId)
            if (result.success) {
                _uiState.update { it.copy(
                    isDeleteTicketConfirmDialogVisible = false,
                    ticketToDeleteId = null,
                    deleteMessage = null
                ) }
                loadDashboardData()
            } else {
                _uiState.update { it.copy(deleteMessage = result.message ?: "Failed to delete ticket") }
            }
        }
    }

    fun deleteEventAndRefresh(eventId: String) {
        val token = tokenProvider() ?: return
        viewModelScope.launch {
            val result = repository.deleteEvent(token, eventId)
            if (result.success) {
                _uiState.update { it.copy(
                    isDeleteEventConfirmDialogVisible = false,
                    eventToDeleteId = null,
                    deleteMessage = null
                ) }
                loadDashboardData()
            } else {
                _uiState.update { it.copy(deleteMessage = result.message ?: "Failed to delete event") }
            }
        }
    }

    fun clearDeleteMessage() {
        _uiState.update { it.copy(deleteMessage = null) }
    }

    fun openAdjustDialog(ticketId: String) {
        _uiState.update { it.copy(ticketToAdjustId = ticketId, isAdjustDialogVisible = true) }
    }

    fun dismissAdjustDialog() {
        _uiState.update { it.copy(ticketToAdjustId = null, isAdjustDialogVisible = false) }
    }

    fun submitAdjustAndRefresh(ticketId: String, adjustBy: Int) {
        val token = tokenProvider() ?: return
        viewModelScope.launch {
            val ticket = uiState.value.ticketList.find { it._id == ticketId }
            if (ticket == null) {
                return@launch
            }
            val currentMax = ticket.maxQuantity ?: 0
            val newMaxQuantity = (currentMax + adjustBy).coerceAtLeast(ticket.ticketsSold)

            repository.adjustPrintedTicketStock(token, ticketId, newMaxQuantity)

            dismissAdjustDialog()
            loadDashboardData()
        }
    }

    private fun loadAllocationsForTicket(ticketId: String) {
        val token = tokenProvider() ?: run {
            return
        }
        viewModelScope.launch {
            val result = repository.getAllocationsForTicket(token, ticketId)
            result.onSuccess { allocations ->
                _uiState.update { state ->
                    state.copy(
                        allocationsPerTicket = state.allocationsPerTicket + (ticketId to allocations)
                    )
                }
            }.onFailure {
                // Empty
            }
        }
    }

    fun submitAssignAndRefresh(request: UpdateAllocationsRequest) {
        val token = tokenProvider() ?: return
        viewModelScope.launch {
            val success = repository.updateAllocationChanceNumbers(
                token,
                request.ticketId,
                request.updates
            )

            if (success) {

                // PENDING COUNTERS STILL WORK
                _uiState.update { state ->
                    val printedTickets = state.ticketList.filter { it.ticketType == TicketType.PRINTED }
                    val pendingPerTicket = printedTickets.associate { it._id to it.pendingActions }
                    val pendingPerEvent = printedTickets
                        .groupBy { it.eventId ?: "" }
                        .mapValues { (_, list) -> list.sumOf { it.pendingActions } }

                    state.copy(
                        pendingAssignmentsPerTicket = pendingPerTicket,
                        pendingAssignmentsPerEvent = pendingPerEvent
                    )
                }

                // 🚀 REFRESH REAL DATA (DO NOT UPDATE allocations locally)
                loadAllocationsForTicketDetail(request.ticketId)
                loadDashboardData()

            } else {
                loadAllocationsForTicketDetail(request.ticketId)
            }
        }
    }

    fun publishEventAndRefresh(eventId: String) {
        val token = tokenProvider() ?: run {
            return
        }

        viewModelScope.launch {
            val success = repository.publishEvent(token, eventId)
            if (success) {
                loadDashboardData()
            } else {
                // Intentionally left blank
            }
        }
    }

    fun loadAllocationsForTicketDetail(ticketId: String) {
        val token = tokenProvider() ?: run {
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isTicketDetailLoading = true, errorMessage = null) }
            val result = repository.getAllocationsForTicketDetail(token, ticketId)
            result.onSuccess { allocations ->
                _uiState.update {
                    val current = it.allocationsPerTicket.toMutableMap()
                    current[ticketId] = allocations
                    it.copy(
                        isTicketDetailLoading = false,
                        allocationsPerTicket = current,
                        errorMessage = null
                    )
                }
            }.onFailure { e ->
                _uiState.update { it.copy(isTicketDetailLoading = false, errorMessage = e.message ?: "Unknown error") }
            }
        }
    }

    fun createPrize(
        eventId: String,
        request: CreatePrizeRequest
    ) {

        val token = tokenProvider() ?: return

        viewModelScope.launch {

            runCatching {

                repository.createPrize(
                    token,
                    eventId,
                    request
                )

            }.onSuccess { prizeId ->

                if (prizeId != null) {

                    _uiState.update {
                        it.copy(
                            prizeToUpdateId = prizeId,
                            isEditingPrize = false
                        )
                    }

                    // Refresh only prizes
                    getPrizesByEventId(eventId)

                    // Open AFTER fetch starts
                    openCreatePrizeDialog(
                        prizeId = prizeId,
                        isEditing = false
                    )
                }

            }.onFailure { error ->

                _uiState.update {
                    it.copy(
                        errorMessage = error.message
                    )
                }
            }
        }
    }

    fun createSizeRaffleFromTicket(eventId: String, request: CreatePrizeRequest) {
        val token = tokenProvider() ?: return
        viewModelScope.launch {
            val prizeId = repository.createPrize(token, eventId, request)
            if (prizeId != null) {
                _uiState.update {
                    it.copy(prizeToUpdateId = prizeId)
                }
            } else return@launch
        }
    }

    fun createPrizeSeries(
        eventId: String,
        request: CreatePrizeSeriesRequest
    ) {

        val token = tokenProvider() ?: return

        viewModelScope.launch {

            runCatching {

                repository.createPrizeSeries(
                    token,
                    eventId,
                    request
                )

            }.onSuccess { prizeIds ->

                if (!prizeIds.isNullOrEmpty()) {

                    _uiState.update {
                        it.copy(
                            isEditingPrize = false,
                            prizeToUpdateId = null
                        )
                    }

                    getPrizesByEventId(eventId)
                }

            }.onFailure { error ->

                _uiState.update {
                    it.copy(
                        errorMessage = error.message
                    )
                }
            }
        }
    }

    fun openCreatePrizeDialog(prizeId: String, isEditing: Boolean) {
        _uiState.update { it.copy(prizeToUpdateId = prizeId, isUpdatePrizeDialogVisible = true, isEditingPrize = isEditing) }
    }

    fun updatePrize(
        prizeId: String,
        request: UpdatePrizeRequest
    ) {
        val token = tokenProvider() ?: return

        val previousPrizeList = _uiState.value.prizeList

        // Optimistic local update
        _uiState.update { state ->
            state.copy(
                prizeList = state.prizeList.map { prize ->

                    if (prize._id == prizeId) {
                        prize.copy(
                            title = request.title ?: prize.title,
                            winningNumber = request.winningNumber ?: prize.winningNumber,
                            serialNumber = request.serialNumber ?: prize.serialNumber,
                            timeSlotInstant = request.timeSlot ?: prize.timeSlotInstant
                        )
                    } else {
                        prize
                    }
                }
            )
        }

        // Background sync
        viewModelScope.launch {

            runCatching {
                repository.updatePrize(
                    token = token,
                    prizeId = prizeId,
                    request = request
                )
            }.onFailure { error ->

                // Rollback if request fails
                _uiState.update {
                    it.copy(
                        prizeList = previousPrizeList,
                        errorMessage = error.message ?: "Failed to update prize"
                    )
                }
            }
        }
    }

    fun dismissCreatePrizeDialog() {
        _uiState.update { it.copy(isUpdatePrizeDialogVisible = false, prizeToUpdateId = null) }
    }

    fun deletePrize(prizeId: String) {

        val token = tokenProvider() ?: return

        val previousPrizeList = _uiState.value.prizeList

        // Optimistic removal
        _uiState.update {
            it.copy(
                prizeList = it.prizeList.filterNot { prize ->
                    prize._id == prizeId
                },
                isDeletePrizeDialogVisible = false,
                prizeToDeleteId = null,
                deleteMessage = null
            )
        }

        viewModelScope.launch {

            val result = repository.deletePrize(token, prizeId)

            if (!result.success) {

                // Rollback on failure
                _uiState.update {
                    it.copy(
                        prizeList = previousPrizeList,
                        deleteMessage = result.message ?: "Failed to delete prize"
                    )
                }
            }
        }
    }

    fun getPrizesByEventId(eventId: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isPrizeListLoading = true, errorMessage = null) }
            val result = repository.getPrizesByEventId(eventId)
            result.onSuccess { response ->
                _uiState.update {
                    it.copy(
                        isPrizeListLoading = false,
                        prizeList = response.prizes,
                        errorMessage = null
                    )
                }
            }.onFailure { e ->
                _uiState.update {
                    it.copy(
                        isPrizeListLoading = false,
                        errorMessage = e.message ?: "Unknown Error"
                    )
                }
            }
        }
    }

    fun showDeletePrizeDialog(prizeId: String) {
        _uiState.update { it.copy(prizeToDeleteId = prizeId, isDeletePrizeDialogVisible = true) }
    }

    private fun handleImageUpload(
        oldUrl: String?,
        newUrl: String,
        onUrlUpdated: (String) -> Unit
    ) {
        if (!oldUrl.isNullOrBlank()) {
            viewModelScope.launch {
                try {
                    repository.deleteImageFromSupabase(oldUrl)
                } catch (_: Exception) {
                    // Ignore errors during deletion
                }
            }
        }
        onUrlUpdated(newUrl)
    }

    fun onEventImageUploaded(newImageUrl: String) {
        val oldUrl = uiState.value.eventImageUrl
        handleImageUpload(oldUrl, newImageUrl) { updatedUrl ->
            _uiState.value = _uiState.value.copy(eventImageUrl = updatedUrl)
        }
    }

    fun onTicketImageUploaded(ticketId: String, newImageUrl: String) {
        val oldUrl = uiState.value.ticketImageMap[ticketId]
        handleImageUpload(oldUrl, newImageUrl) { updatedUrl ->
            _uiState.update {
                it.copy(
                    ticketImageMap = it.ticketImageMap + (ticketId to updatedUrl),
                    ticketImageUrl = updatedUrl
                )
            }
        }
    }

}